import type { Block, BlockOf } from '@ads/shared';
import { SITE_CSS } from './css';
import { anchor, esc, isExternal, rich, safeHref } from './html';

/* ══════════════════════════════════════════════════════════════════
   Blocos tipados → páginas estáticas.
   O mesmo código desenha o canvas do Studio (modo preview, estilos
   em linha, assets servidos pela API) e o pacote publicado (modo
   publish, CSS e assets como arquivos). O que o cliente aprova é,
   byte a byte, o que vai para o ar.
   ══════════════════════════════════════════════════════════════════ */

export interface SiteDs {
  /** os 16 primeiros caracteres do hash da versão (escopo do CSS) */
  hash: string;
  css: string;
  logo: { primary: string | null; mono: string | null };
  themeColor?: string | null;
}

export interface SiteInput {
  /** título da peça: vira o título da aba na página inicial */
  title: string;
  blocks: readonly Block[];
  org: { name: string };
  ds: SiteDs | null;
  mode: 'preview' | 'publish';
  /** para onde vão os botões sem link próprio (briefing) */
  primaryLink?: string;
  /** blocos a partir deste índice chegam animados (canvas ao vivo) */
  newFrom?: number;
  /** identifica o pacote publicado: o publicador confere no ar */
  bundleId?: string;
  year?: number;
  lang?: string;
  /** URL pública do site, quando já se sabe (canonical e og:url) */
  siteUrl?: string;
}

export interface SitePage {
  slug: string;
  title: string;
  /** caminho do arquivo no pacote: index.html, sobre/index.html… */
  file: string;
  /** caminho público: /, /sobre/ */
  href: string;
  html: string;
  /** o que vai dentro de <head>, para o canvas saber quando recarregar */
  head: string;
  /** o que vai dentro de <body>, para o canvas trocar sem piscar */
  body: string;
  htmlAttrs: string;
}

export interface SiteAsset {
  /** chave no storage do ADS (ds/<org>/<hash>/<rel>) */
  key: string;
  /** caminho no pacote (assets/ds/<rel>) */
  file: string;
}

export interface SiteOutput {
  pages: SitePage[];
  /** CSS completo (site + design system), com URLs já reescritas */
  css: string;
  /** nome do arquivo de CSS no pacote (modo publish) */
  cssFile: string;
  assets: SiteAsset[];
}

/* ── utilidades ─────────────────────────────────────────────────── */

/** FNV-1a 32 bits: chave curta e estável para cada faixa. */
export function hashStr(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}

/** Rótulo curto para a navegação: o título, se couber; senão, o nome da faixa. */
const navLabel = (title: string | undefined, fallback: string) => {
  const t = title ? plain(title) : '';
  return t && t.length <= 18 ? t : fallback;
};
const plain = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\s+/g, ' ').trim();
function clip(s: string, n: number) {
  const p = plain(s);
  if (p.length <= n) return p;
  const cut = p.slice(0, n - 1);
  return cut.slice(0, Math.max(cut.lastIndexOf(' '), n * .6)).replace(/[\s,.;:—-]+$/, '') + '…';
}

const ARROW = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg>';

/* ── assets do design system ────────────────────────────────────── */

const ASSET_RE = /\/api\/assets\/(ds\/[^/"')\s]+\/[^/"')\s]+)\/([^"')\s?#]+)/g;

/** Troca a URL da API pelo caminho no pacote. `prefix` é relativo a quem
    referencia: o CSS mora em assets/, a página pode estar um nível abaixo. */
function rewriteAssets(text: string, found: Map<string, SiteAsset>, prefix: string): string {
  return text.replace(ASSET_RE, (_m, base: string, rel: string) => {
    const relDec = rel.split('/').map(decodeURIComponent).join('/');
    const baseDec = base.split('/').map(decodeURIComponent);
    /* a versão do design system entra no caminho: cache imutável sem colisão entre versões */
    const ver = encodeURIComponent((baseDec[2] ?? 'v').slice(0, 8));
    const key = `${baseDec.join('/')}/${relDec}`;
    found.set(key, { key, file: `assets/ds/${decodeURIComponent(ver)}/${relDec}` });
    return `${prefix}${ver}/${rel}`;
  });
}

/* ── páginas ────────────────────────────────────────────────────── */

interface Item { b: Block; i: number }
interface PageDraft { slug: string; title: string; items: Item[] }

export function splitPages(blocks: readonly Block[]): PageDraft[] {
  const pages: PageDraft[] = [];
  const used = new Set<string>();
  const uniq = (want: string) => {
    let s = want, n = 2;
    while (used.has(s)) s = `${want || 'pagina'}-${n++}`;
    used.add(s);
    return s;
  };
  let cur: PageDraft | null = null;
  blocks.forEach((b, i) => {
    if (b.type === 'page') {
      /* a primeira página sem conteúdo anterior assume a inicial */
      if (cur && cur.slug === '' && cur.items.length === 0 && b.slug === '') { cur.title = b.title; return; }
      const want = b.slug === '' && !used.has('') ? '' : (b.slug || anchor(b.title));
      cur = { slug: uniq(want), title: b.title, items: [] };
      pages.push(cur);
      return;
    }
    if (!cur) { cur = { slug: uniq(''), title: 'Início', items: [] }; pages.push(cur); }
    cur.items.push({ b, i });
  });
  if (!pages.length) pages.push({ slug: '', title: 'Início', items: [] });
  if (!pages.some(p => p.slug === '')) pages[0]!.slug = '';
  return pages;
}

/** Caminho público de uma página (para canonical e para a barra do canvas). */
const pageHref = (slug: string) => (slug ? `/${slug}/` : '/');
/* Links internos são relativos: o mesmo pacote funciona na raiz de um
   domínio, numa subpasta do GitHub Pages ou na prévia da API. */
const upOf = (slug: string) => (slug ? '../' : '');
const linkTo = (from: string, to: string) => `${upOf(from)}${to ? `${to}/` : ''}` || './';
const pageFile = (slug: string) => (slug ? `${slug}/index.html` : 'index.html');

/* ── faixas ─────────────────────────────────────────────────────── */

interface Band { html: string; id?: string; label?: string }

interface Ctx {
  input: SiteInput;
  pages: PageDraft[];
  slug: string;
  isHome: boolean;
  newFrom: number;
  primary: string;
}

const isNew = (ctx: Ctx, i: number) => (i >= ctx.newFrom ? ' data-new' : '');

function linkAttrs(ctx: Ctx, href: string): string {
  if (isExternal(href)) return ` href="${esc(href)}" target="_blank" rel="noopener"`;
  const m = /^\/([a-z0-9-]*)\/?$/.exec(href);
  if (m && ctx.pages.some(p => p.slug === m[1])) return ` href="${esc(linkTo(ctx.slug, m[1]!))}" data-page="${esc(m[1]!)}"`;
  return ` href="${esc(href)}"`;
}

const hrefOf = (ctx: Ctx, l?: { href?: string }) => safeHref(l?.href || ctx.primary);

function button(ctx: Ctx, l: { label: string; href?: string } | undefined, kind: 'a' | 'ghost' | 'p', extra = '') {
  if (!l) return '';
  return `<a class="s-btn s-btn-${kind}${extra}"${linkAttrs(ctx, hrefOf(ctx, l))}>${esc(l.label)}${kind === 'ghost' ? '' : ARROW}</a>`;
}

function hero(ctx: Ctx, it: Item & { b: BlockOf<'hero'> }): Band {
  const b = it.b;
  const actions = b.cta || b.secondary
    ? `<div class="s-actions">${button(ctx, b.cta, 'a')}${button(ctx, b.secondary, 'ghost')}</div>` : '';
  return {
    html: `<section class="s-hero${ctx.isHome ? '' : ' sm'}"${isNew(ctx, it.i)}>
<div class="s-hero-art" aria-hidden="true"><i></i><i></i><i></i></div>
<div class="s-wrap s-hero-in">${b.eyebrow ? `<p class="s-eyebrow">${esc(b.eyebrow)}</p>` : ''}<h1>${rich(b.title)}</h1>${b.subtitle ? `<p class="s-lead">${rich(b.subtitle)}</p>` : ''}${actions}</div>
</section>`,
  };
}

function pageHeader(ctx: Ctx, title: string, eyebrow: string): Band {
  return {
    html: `<section class="s-hero sm">
<div class="s-hero-art" aria-hidden="true"><i></i><i></i><i></i></div>
<div class="s-wrap s-hero-in"><p class="s-eyebrow">${esc(eyebrow)}</p><h1>${esc(title)}</h1></div>
</section>`,
  };
}

function bandHead(title?: string, intro?: string, h = 'h2') {
  if (!title && !intro) return '';
  return `<div class="s-band-h">${title ? `<${h}>${rich(title)}</${h}>` : ''}${intro ? `<p>${rich(intro)}</p>` : ''}</div>`;
}

function features(ctx: Ctx, it: Item & { b: BlockOf<'features'> }): Band {
  const b = it.b;
  const id = anchor(b.title ? plain(b.title) : 'diferenciais');
  const cards = b.items.map((x, n) => `<article class="s-card s-reveal"><span class="s-card-no">${String(n + 1).padStart(2, '0')}</span><h3>${rich(x.title)}</h3><p>${rich(x.text)}</p></article>`).join('');
  return {
    id, label: navLabel(b.title, 'Diferenciais'),
    html: `<section class="s-band" id="${id}"${isNew(ctx, it.i)}><div class="s-wrap">${bandHead(b.title, b.intro)}<div class="s-grid${b.items.length === 4 ? ' n2' : ''}">${cards}</div></div></section>`,
  };
}

function steps(ctx: Ctx, it: Item & { b: BlockOf<'steps'> }): Band {
  const b = it.b;
  const title = b.title ?? 'Como funciona';
  const id = anchor(plain(title));
  const lis = b.items.map(x => `<li class="s-reveal"><h3>${rich(x.title)}</h3><p>${rich(x.text)}</p></li>`).join('');
  return {
    id, label: navLabel(title, 'Como funciona'),
    html: `<section class="s-band alt" id="${id}"${isNew(ctx, it.i)}><div class="s-wrap">${bandHead(title)}<ol class="s-steps">${lis}</ol></div></section>`,
  };
}

function stats(ctx: Ctx, it: Item & { b: BlockOf<'stats'> }): Band {
  const cells = it.b.items.map(x => `<div class="s-stat s-reveal"><b>${esc(x.value)}</b><span>${rich(x.label)}</span></div>`).join('');
  return { html: `<section class="s-band"${isNew(ctx, it.i)}><div class="s-wrap"><div class="s-stats">${cells}</div></div></section>` };
}

function testimonial(ctx: Ctx, it: Item & { b: BlockOf<'testimonial'> }): Band {
  const b = it.b;
  return {
    html: `<section class="s-band wash"${isNew(ctx, it.i)}><div class="s-wrap"><figure class="s-quote s-reveal"><blockquote>${rich(b.quote)}</blockquote><figcaption><b>${esc(b.author)}</b>${b.role ? esc(b.role) : ''}</figcaption></figure></div></section>`,
  };
}

function faq(ctx: Ctx, it: Item & { b: BlockOf<'faq'> }): Band {
  const b = it.b;
  const title = b.title ?? 'Perguntas frequentes';
  const list = b.items.map((x, n) => `<details${n === 0 ? ' open' : ''}><summary>${esc(plain(x.q))}</summary><p>${rich(x.a)}</p></details>`).join('');
  return {
    id: 'perguntas', label: 'Perguntas',
    html: `<section class="s-band" id="perguntas"${isNew(ctx, it.i)}><div class="s-wrap s-faq">${bandHead(title, 'Não achou o que procurava? Fale com a gente — respondemos rápido.')}<div class="s-faq-list">${list}</div></div></section>`,
  };
}

function cta(ctx: Ctx, it: Item & { b: BlockOf<'cta'> }): Band {
  const b = it.b;
  return {
    id: 'contato', label: 'Contato',
    html: `<section class="s-cta" id="contato"${isNew(ctx, it.i)}><div class="s-wrap"><h2>${rich(b.title)}</h2>${b.text ? `<p>${rich(b.text)}</p>` : ''}<div class="s-actions">${button(ctx, b.button, 'a')}</div></div></section>`,
  };
}

/* conteúdo corrido: blocos de documento dentro de uma faixa */
class Prose {
  parts: string[] = [];
  metrics: string[] = [];
  id?: string;
  label?: string;
  first: number;
  constructor(first: number, title?: string, id?: string) {
    this.first = first;
    if (title) {
      this.id = id; this.label = navLabel(title, clip(title, 18));
      this.parts.push(`<h2>${rich(title)}</h2>`);
    }
  }
  flushMetrics() {
    if (!this.metrics.length) return;
    this.parts.push(`<div class="s-metrics">${this.metrics.join('')}</div>`);
    this.metrics = [];
  }
  add(ctx: Ctx, it: Item) {
    const b = it.b, n = isNew(ctx, it.i);
    if (b.type === 'metric') {
      const v = typeof b.value === 'number' ? b.value.toLocaleString('pt-BR') : b.value;
      this.metrics.push(`<div${n}><b>${esc(String(v))}${b.unit ? esc(b.unit) : ''}</b><span>${esc(b.label)}${b.delta ? ` · ${esc(b.delta)}` : ''}</span></div>`);
      return;
    }
    this.flushMetrics();
    switch (b.type) {
      case 'heading': this.parts.push(`<h${b.level}${n}>${rich(b.text)}</h${b.level}>`); break;
      case 'paragraph': this.parts.push(`<p${n}>${rich(b.text)}</p>`); break;
      case 'list': {
        const tag = b.style === 'number' ? 'ol' : 'ul';
        this.parts.push(`<${tag}${n}>${b.items.map(x => `<li>${rich(x)}</li>`).join('')}</${tag}>`);
        break;
      }
      case 'callout': this.parts.push(`<p class="s-note"${n}>${rich(b.text)}</p>`); break;
      case 'quote': this.parts.push(`<blockquote${n}>${rich(b.text)}${b.cite ? `<cite>${esc(b.cite)}</cite>` : ''}</blockquote>`); break;
      case 'table': {
        const head = b.columns.map(c => `<th scope="col">${esc(c)}</th>`).join('');
        const rows = b.rows.map(r => `<tr>${r.map(c => `<td>${rich(c)}</td>`).join('')}</tr>`).join('');
        this.parts.push(`<div class="s-table"${n}><table>${b.caption ? `<caption>${esc(b.caption)}</caption>` : ''}<thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`);
        break;
      }
      case 'chart': {
        const max = Math.max(...b.series.map(s => Math.abs(s.value)), 1);
        const rows = b.series.map(s => {
          const pct = Math.round((Math.abs(s.value) / max) * 100);
          return `<div><span>${esc(s.label)}</span><i style="--v:${pct}%"></i><span>${esc(s.value.toLocaleString('pt-BR'))}${b.unit ? esc(b.unit) : ''}</span></div>`;
        }).join('');
        this.parts.push(`${b.title ? `<h3${n}>${esc(b.title)}</h3>` : ''}<div class="s-bars"${n}>${rows}</div>`);
        break;
      }
      default: break;
    }
  }
  band(ctx: Ctx): Band {
    this.flushMetrics();
    return {
      id: this.id, label: this.label,
      html: `<section class="s-band"${this.id ? ` id="${this.id}"` : ''}${isNew(ctx, this.first)}><div class="s-wrap"><div class="s-prose">${this.parts.join('')}</div></div></section>`,
    };
  }
}

function bands(ctx: Ctx, page: PageDraft): Band[] {
  const out: Band[] = [];
  let prose: Prose | null = null;
  const close = () => { if (prose) { out.push(prose.band(ctx)); prose = null; } };
  for (const it of page.items) {
    const b = it.b;
    switch (b.type) {
      case 'hero': close(); out.push(hero(ctx, it as Item & { b: typeof b })); break;
      case 'features': close(); out.push(features(ctx, it as Item & { b: typeof b })); break;
      case 'steps': close(); out.push(steps(ctx, it as Item & { b: typeof b })); break;
      case 'stats': close(); out.push(stats(ctx, it as Item & { b: typeof b })); break;
      case 'testimonial': close(); out.push(testimonial(ctx, it as Item & { b: typeof b })); break;
      case 'faq': close(); out.push(faq(ctx, it as Item & { b: typeof b })); break;
      case 'cta': close(); out.push(cta(ctx, it as Item & { b: typeof b })); break;
      case 'section': close(); prose = new Prose(it.i, b.title, anchor(b.id || b.title)); break;
      case 'page': break;
      default:
        if (!prose) prose = new Prose(it.i);
        prose.add(ctx, it);
    }
  }
  close();
  /* toda página começa com um cabeçalho, mesmo que o agente esqueça */
  if (!page.items.some(it => it.b.type === 'hero') && (page.items.length || !ctx.isHome)) {
    out.unshift(pageHeader(ctx, ctx.isHome ? ctx.input.title : page.title, ctx.input.org.name));
  }
  return out;
}

/* ── moldura: navegação e rodapé ────────────────────────────────── */

function firstLink(pages: PageDraft[]): { label: string; href?: string } | undefined {
  for (const p of pages) for (const { b } of p.items) {
    if (b.type === 'hero' && b.cta) return b.cta;
    if (b.type === 'cta') return b.button;
  }
  return undefined;
}

function brand(ctx: Ctx, logo: string | null) {
  const org = esc(ctx.input.org.name);
  return `<a class="s-brand" href="${esc(linkTo(ctx.slug, ''))}" data-page="">${logo ? `<img src="${esc(logo)}" alt="" width="34" height="34">` : ''}<span>${org}</span></a>`;
}

function contactLabel(href: string, fallback: string) {
  if (href.startsWith('mailto:')) return href.slice(7).split('?')[0]!;
  if (href.startsWith('tel:')) return href.slice(4);
  if (/wa\.me|whatsapp/i.test(href)) return 'WhatsApp';
  return fallback;
}

function renderPage(input: SiteInput, pages: PageDraft[], page: PageDraft, rawLogos: { primary: string | null; mono: string | null }, cssFile: string | null, css: string) {
  const isHome = page.slug === '';
  const up = upOf(page.slug);
  /* no pacote, o logo é relativo à página; na prévia, a URL da API vale como está */
  const logos = cssFile
    ? { primary: rawLogos.primary && up + rawLogos.primary, mono: rawLogos.mono && up + rawLogos.mono }
    : rawLogos;
  const cssHref = cssFile ? up + cssFile : null;
  const primaryRaw = input.primaryLink?.trim() || firstLink(pages)?.href || '';
  const ctx: Ctx = { input, pages, slug: page.slug, isHome, newFrom: input.newFrom ?? Infinity, primary: safeHref(primaryRaw) };
  const bs = bands(ctx, page);
  const lead = firstLink(pages);

  /* navegação: páginas do site ou seções da landing */
  const multi = pages.length > 1;
  const navItems: Array<{ label: string; href: string; slug?: string }> = multi
    ? pages.map(p => ({ label: p.title, href: linkTo(page.slug, p.slug), slug: p.slug as string | undefined }))
    : bs.filter(b => b.id && b.label && b.id !== 'contato').slice(0, 4).map(b => ({ label: b.label!, href: `#${b.id}` }));
  const links = navItems.map(n => `<a href="${esc(n.href)}"${n.slug !== undefined ? ` data-page="${esc(n.slug)}"` : ''}${n.slug === page.slug && multi ? ' aria-current="page"' : ''}>${esc(n.label)}</a>`).join('');
  /* no celular, os links viram um menu — <details>, sem uma linha de script */
  const menu = navItems.length ? `<details class="s-menu"><summary aria-label="Menu"><i></i></summary><nav class="s-menu-p" aria-label="Menu">${links}</nav></details>` : '';
  const nav = `<header class="s-nav"><div class="s-wrap s-nav-in">${brand(ctx, logos.primary)}<nav class="s-links" aria-label="Principal">${links}</nav>${lead ? button(ctx, lead, 'a', ' s-btn-sm') : ''}${menu}</div></header>`;

  /* rodapé */
  const homeHero = pages[0]?.items.find(it => it.b.type === 'hero')?.b as BlockOf<'hero'> | undefined;
  /* no rodapé, só a primeira frase do herói */
  const firstSentence = homeHero?.subtitle ? plain(homeHero.subtitle).split(/(?<=[.!?])\s+/)[0]! : '';
  const tagline = firstSentence ? clip(firstSentence, 150) : '';
  const footNav = (multi || navItems.length ? navItems : [{ label: 'Início', href: linkTo(page.slug, ''), slug: '' }])
    .map(n => `<li><a href="${esc(n.href)}"${n.slug !== undefined ? ` data-page="${esc(n.slug)}"` : ''}>${esc(n.label)}</a></li>`).join('');
  const contact = ctx.primary && ctx.primary !== '#contato'
    ? `<div><h4>Fale conosco</h4><ul><li><a${linkAttrs(ctx, ctx.primary)}>${esc(contactLabel(ctx.primary, lead?.label ?? 'Fale conosco'))}</a></li></ul></div>`
    : '<div></div>';
  const hasContato = bs.some(b => b.id === 'contato');
  const year = input.year ?? new Date().getFullYear();
  const foot = `<footer class="s-foot"${hasContato ? '' : ' id="contato"'}><div class="s-wrap s-foot-in"><div>${brand({ ...ctx }, logos.mono ?? logos.primary)}${tagline ? `<p>${esc(tagline)}</p>` : ''}</div><div><h4>Navegue</h4><ul>${footNav}</ul></div>${contact}</div><div class="s-wrap s-foot-base"><span>© ${year} ${esc(input.org.name)}</span><span>Feito com ADS · Aion</span></div></footer>`;

  const key = (html: string) => html.replace(/^<(\w+)/, `<$1 data-k="${hashStr(html)}"`);
  const body = `<a class="s-skip" href="#conteudo">Pular para o conteúdo</a>\n${key(nav)}\n<main id="conteudo">\n${bs.map(b => key(b.html)).join('\n')}\n</main>\n${key(foot)}`;

  /* cabeçalho do documento */
  const org = input.org.name;
  const title = isHome ? `${org} — ${plain(homeHero?.title ?? input.title)}` : `${page.title} · ${org}`;
  const desc = clip((isHome ? homeHero?.subtitle : undefined) || tagline || input.title, 158);
  const url = input.siteUrl ? input.siteUrl.replace(/\/$/, '') + pageHref(page.slug) : null;
  const icon = logos.primary && /\.svg($|\?)/i.test(logos.primary) ? logos.primary : null;
  const head = [
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    `<title>${esc(clip(title, 90))}</title>`,
    desc ? `<meta name="description" content="${esc(desc)}">` : '',
    `<meta property="og:title" content="${esc(clip(title, 90))}">`,
    desc ? `<meta property="og:description" content="${esc(desc)}">` : '',
    '<meta property="og:type" content="website">',
    url ? `<meta property="og:url" content="${esc(url)}"><link rel="canonical" href="${esc(url)}">` : '',
    input.ds?.themeColor ? `<meta name="theme-color" content="${esc(input.ds.themeColor)}">` : '',
    '<meta name="generator" content="ADS · Aion">',
    input.bundleId ? `<meta name="ads-bundle" content="${esc(input.bundleId)}">` : '',
    icon ? `<link rel="icon" href="${esc(icon)}" type="image/svg+xml">` : '',
    cssHref ? `<link rel="stylesheet" href="${esc(cssHref)}">` : `<style>${css}</style>`,
  ].filter(Boolean).join('\n');

  const htmlAttrs = `lang="${esc(input.lang ?? 'pt-BR')}"${input.ds ? ` data-ds="${esc(input.ds.hash.slice(0, 16))}"` : ''}`;
  const html = `<!doctype html>\n<html ${htmlAttrs}>\n<head>\n${head}\n</head>\n<body>\n${body}\n</body>\n</html>\n`;
  return { slug: page.slug, title: page.title, file: pageFile(page.slug), href: pageHref(page.slug), html, head, body, htmlAttrs };
}

export function renderSite(input: SiteInput): SiteOutput {
  const pages = splitPages(input.blocks);
  const found = new Map<string, SiteAsset>();
  const publish = input.mode === 'publish';

  const dsCss = input.ds?.css ?? '';
  const rawCss = `${dsCss}\n${SITE_CSS}`;
  /* o CSS mora em assets/: as fontes ficam em assets/ds/, ou seja, "ds/…" */
  const css = publish ? rewriteAssets(rawCss, found, 'ds/') : rawCss;
  const logos = {
    primary: input.ds?.logo.primary ? (publish ? rewriteAssets(input.ds.logo.primary, found, 'assets/ds/') : input.ds.logo.primary) : null,
    mono: input.ds?.logo.mono ? (publish ? rewriteAssets(input.ds.logo.mono, found, 'assets/ds/') : input.ds.logo.mono) : null,
  };
  const cssFile = `assets/site.${hashStr(css)}.css`;
  /* `<style>` não pode ser fechado de dentro: nada do CSS escapa */
  const inline = css.replace(/<\/style/gi, '<\\/style');

  return {
    pages: pages.map(p => renderPage(input, pages, p, logos, publish ? cssFile : null, inline)),
    css, cssFile, assets: [...found.values()],
  };
}
